import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Backspace, LockKey } from "@phosphor-icons/react";

export default function PunchLogin({ onLoggedIn }) {
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (p = pin) => {
    if (!code.trim() || p.length < 4) return;
    setBusy(true); setErr("");
    try {
      await api.post("/punch/login", { emp_code: code.trim(), pin: p });
      onLoggedIn();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPin("");
    } finally { setBusy(false); }
  };

  const press = (d) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4 && code.trim()) setTimeout(() => submit(next), 120);
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-10 pb-8" data-testid="punch-login">
      <div className="flex items-center gap-3">
        <img src="/brand/celsius-mark.png" alt="Celsius" className="w-10 h-10 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#EA580C]">Celsius · Attendance</div>
          <h1 className="text-2xl font-bold tracking-tight leading-tight">Punch In / Out</h1>
        </div>
      </div>

      <label className="block mt-8">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-1">Employee code</div>
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CEL-001" autoCapitalize="characters" autoComplete="username" inputMode="text"
               className="w-full h-12 rounded-sm border border-slate-300 bg-white px-3 text-lg font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-[#EA580C]" data-testid="punch-code-input" />
      </label>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2 flex items-center gap-1"><LockKey size={12} /> 4-digit PIN</div>
        <div className="flex gap-3 justify-center mb-4" data-testid="punch-pin-dots">
          {[0, 1, 2, 3].map((i) => <span key={i} className={`w-4 h-4 rounded-full border-2 transition-colors ${pin.length > i ? "bg-[#0F172A] border-[#0F172A]" : "border-slate-300 bg-white"}`} />)}
        </div>
        <input type="password" inputMode="numeric" pattern="[0-9]*" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} onKeyDown={(e) => e.key === "Enter" && submit()}
               className="sr-only" aria-label="PIN" data-testid="punch-pin-input" />
        <div className="grid grid-cols-3 gap-2" data-testid="punch-keypad">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button key={d} onClick={() => press(d)} disabled={busy} className="h-14 rounded-sm bg-white border border-slate-200 text-xl font-semibold active:bg-slate-100 active:scale-[0.98] transition-transform" data-testid={`punch-key-${d}`}>{d}</button>
          ))}
          <button onClick={() => setPin("")} disabled={busy} className="h-14 rounded-sm text-[11px] uppercase tracking-widest text-slate-500" data-testid="punch-key-clear">Clear</button>
          <button onClick={() => press("0")} disabled={busy} className="h-14 rounded-sm bg-white border border-slate-200 text-xl font-semibold active:bg-slate-100" data-testid="punch-key-0">0</button>
          <button onClick={() => setPin(pin.slice(0, -1))} disabled={busy} className="h-14 rounded-sm text-slate-500 flex items-center justify-center" data-testid="punch-key-back"><Backspace size={22} /></button>
        </div>
      </div>

      {err && <div className="mt-4 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2" data-testid="punch-login-error">{err}</div>}

      <button onClick={() => submit()} disabled={busy || pin.length < 4 || !code.trim()} className="mt-5 h-12 rounded-full bg-[#EA580C] text-white font-semibold text-[15px] disabled:opacity-40 active:scale-[0.99] transition-transform" data-testid="punch-login-btn">
        {busy ? "Signing in…" : "Continue"}
      </button>
      <div className="mt-auto pt-8 text-center text-[11px] text-slate-400">Your PIN is set by the Celsius admin. Punch times are recorded by the server clock.</div>
    </div>
  );
}
